import CharLimitLabel from '@/components/base/CharLimitLabel';
import { useState } from 'react';
import type { ContentSection } from '@/mocks/websiteBuilder';
import IconPicker from '@/components/base/IconPicker';
import ImageUploader from '@/components/base/ImageUploader';

interface ServiceProductSectionProps {
  section: ContentSection;
  editing: boolean;
  editValues: Record<string, string>;
  errors: Record<string, { message: string }>;
  onEditValueChange: (key: string, value: string, max: number) => void;
  onAddItem?: () => void;
  onDeleteItem?: (idx: number) => void;
  onImageUpload?: (key: string, file: File, dataUrl: string) => void;
}

const LIMITS = {
  psHeadline: { min: 10, max: 100 },
  psPain: { min: 10, max: 120 },
  psSolution: { min: 10, max: 200 },
  cardName: { min: 2, max: 50 },
  cardDesc: { min: 10, max: 200 },
  cardUrl: { min: 1, max: 200 },
  testimonialQuote: { min: 20, max: 300 },
  testimonialClient: { min: 2, max: 50 },
  testimonialRole: { min: 2, max: 80 },
  faqQuestion: { min: 10, max: 150 },
  faqAnswer: { min: 20, max: 500 },
  clientName: { min: 2, max: 50 },
};

function parseCardSafely(raw: any, defaultFallback: any) {
  if (typeof raw === 'object' && raw !== null) return raw;
  if (typeof raw !== 'string') return defaultFallback;
  try {
    const res = JSON.parse(raw);
    return typeof res === 'object' && res !== null ? res : defaultFallback;
  } catch {
    return defaultFallback;
  }
}

export default function ServiceProductSection({
  section,
  editing,
  editValues,
  errors,
  onEditValueChange,
  onAddItem,
  onDeleteItem,
  onImageUpload,
}: ServiceProductSectionProps) {
  const [openFaq, setOpenFaq] = useState<number | null>(0);

  const getInputClass = (key: string, base: string) => {
    const hasError = !!errors?.[key];
    return `${base} ${hasError ? 'border-red-400 focus:border-red-400 bg-red-50/30' : 'border-gray-300 focus:border-gold'}`;
  };

  switch (section.type) {
    case 'problemSolution': {
      const data = Array.isArray(section.content) ? section.content : [];
      const raw = data[0] ?? '{}';
      const parsed = parseCardSafely(raw, { headline: '', problems: [] });
      const problems: { pain: string; solution: string }[] = parsed.problems ?? [];
      const headlineKey = 'ps_headline';
      const headlineVal = String(editing ? (editValues[headlineKey] ?? parsed.headline) : parsed.headline || '');

      if (!editing && !headlineVal && problems.length === 0) {
        return (
          <div className="bg-gray-50/70 border border-dashed border-gray-200 rounded-xl p-6 flex flex-col items-center justify-center text-center">
            <p className="text-xs text-gray-500">No problem-solution items configured yet. Click "Generate Section" or "Edit" to add content.</p>
          </div>
        );
      }

      return (
        <div className="space-y-4">
          {editing ? (
            <div>
              <label className="block text-xs text-gray-500 mb-1">Headline</label>
              <input
                className={`w-full text-base font-semibold text-gray-900 rounded px-3 py-2 focus:outline-none border ${getInputClass(headlineKey, '')}`}
                value={headlineVal}
                onChange={(e) => onEditValueChange(headlineKey, e.target.value, LIMITS.psHeadline.max)}
                maxLength={LIMITS.psHeadline.max}
                placeholder="Tired of...?"
              />
              <CharLimitLabel current={headlineVal.length} min={LIMITS.psHeadline.min} max={LIMITS.psHeadline.max} />
              {errors[headlineKey] && (
                <p className="text-[11px] text-red-500 mt-1 flex items-center gap-1">
                  <i className="ri-error-warning-line"></i>{errors[headlineKey].message}
                </p>
              )}
            </div>
          ) : (
            <h3 className="text-lg font-semibold text-gray-900">{String(parsed.headline || '')}</h3>
          )}
          <div className="space-y-3">
            {problems.map((p, idx) => {
              const painKey = `ps_pain_${idx}`;
              const solKey = `ps_sol_${idx}`;
              const painVal = String(editing ? (editValues[painKey] ?? p.pain) : p.pain || '');
              const solVal = String(editing ? (editValues[solKey] ?? p.solution) : p.solution || '');
              return (
                <div key={idx} className="flex gap-3 items-start bg-white border border-gray-200 rounded-lg p-3">
                  <div className="w-6 h-6 rounded-full bg-red-50 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <i className="ri-close-circle-line text-red-400 text-xs"></i>
                  </div>
                  <div className="flex-1 min-w-0">
                    {editing ? (
                      <>
                        <input
                          className={`w-full text-sm font-medium text-gray-900 rounded px-2 py-1 focus:outline-none mb-1 border ${getInputClass(painKey, '')}`}
                          value={painVal}
                          onChange={(e) => onEditValueChange(painKey, e.target.value, LIMITS.psPain.max)}
                          maxLength={LIMITS.psPain.max}
                          placeholder="Pain point"
                        />
                        <CharLimitLabel current={painVal.length} min={LIMITS.psPain.min} max={LIMITS.psPain.max} />
                        {errors[painKey] && (
                          <p className="text-[11px] text-red-500 mt-0.5 flex items-center gap-1">
                            <i className="ri-error-warning-line"></i>{errors[painKey].message}
                          </p>
                        )}
                      </>
                    ) : (
                      <p className="text-sm font-medium text-gray-900">{String(p.pain || '')}</p>
                    )}
                  </div>
                  <div className="w-6 h-6 rounded-full bg-emerald-50 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <i className="ri-arrow-right-line text-emerald-500 text-xs"></i>
                  </div>
                  <div className="flex-1 min-w-0">
                    {editing ? (
                      <>
                        <input
                          className={`w-full text-sm text-gray-700 rounded px-2 py-1 focus:outline-none border ${getInputClass(solKey, '')}`}
                          value={solVal}
                          onChange={(e) => onEditValueChange(solKey, e.target.value, LIMITS.psSolution.max)}
                          maxLength={LIMITS.psSolution.max}
                          placeholder="Solution"
                        />
                        <CharLimitLabel current={solVal.length} min={LIMITS.psSolution.min} max={LIMITS.psSolution.max} />
                        {errors[solKey] && (
                          <p className="text-[11px] text-red-500 mt-0.5 flex items-center gap-1">
                            <i className="ri-error-warning-line"></i>{errors[solKey].message}
                          </p>
                        )}
                      </>
                    ) : (
                      <p className="text-sm text-gray-700">{String(p.solution || '')}</p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      );
    }

    case 'serviceCard':
    case 'productCard': {
      const cards = Array.isArray(section.content) ? section.content : [];
      const label = section.type === 'serviceCard' ? 'Service' : 'Product';
      if (!editing && cards.length === 0) {
        return (
          <div className="bg-gray-50/70 border border-dashed border-gray-200 rounded-xl p-6 flex flex-col items-center justify-center text-center">
            <p className="text-xs text-gray-500">No {label.toLowerCase()} cards configured yet. Click "Generate Section" or "Edit" to add {label.toLowerCase()}s.</p>
          </div>
        );
      }
      return (
        <div>
          <div className="grid grid-cols-2 gap-3">
            {cards.map((card, idx) => {
              const parsed = parseCardSafely(card, { name: typeof card === 'string' ? card : '', description: '', url: '', icon: 'ri-briefcase-line', image: '', priority: idx + 1 });
              const nameKey = `card_${idx}_name`;
              const descKey = `card_${idx}_desc`;
              const urlKey = `card_${idx}_url`;
              const iconKey = `card_${idx}_icon`;
              const imageKey = `card_${idx}_image`;
              const nameVal = String(editing ? (editValues[nameKey] ?? parsed.name) : parsed.name || '');
              const descVal = String(editing ? (editValues[descKey] ?? parsed.description) : parsed.description || '');
              const urlVal = String(editing ? (editValues[urlKey] ?? parsed.url) : parsed.url || '');
              const iconVal = String(editing ? (editValues[iconKey] ?? parsed.icon) : parsed.icon || '');
              const imageVal = String(editing ? (editValues[imageKey] ?? parsed.image) : parsed.image || '');
              const isProduct = section.type === 'productCard';

              return (
                <div key={idx} className="bg-white border border-gray-200 rounded-lg p-4 relative group hover:border-gold/40 transition-colors">
                  {editing && onDeleteItem && (
                    <button
                      onClick={() => onDeleteItem(idx)}
                      className="absolute top-2 right-2 w-6 h-6 flex items-center justify-center rounded-full bg-red-50 text-red-500 hover:bg-red-100 transition-colors cursor-pointer opacity-0 group-hover:opacity-100 z-10"
                      title={`Delete ${label}`}
                    >
                      <i className="ri-delete-bin-line text-xs"></i>
                    </button>
                  )}
                  
                  {isProduct ? (
                    editing ? (
                      <div className="relative group/img mb-3">
                        {imageVal ? (
                          <div className="w-full h-32 bg-white rounded border border-gray-100 flex items-center justify-center overflow-hidden mb-2">
                            <img src={imageVal} alt={nameVal} className="w-full h-full object-cover" />
                          </div>
                        ) : (
                          <div className="w-full h-32 bg-gray-50 rounded border border-gray-100 flex flex-col items-center justify-center text-gray-300 mb-2">
                            <i className="ri-image-add-line text-2xl mb-1"></i>
                            <span className="text-[10px]">Upload Image</span>
                          </div>
                        )}
                        <div className="mt-2">
                          <ImageUploader 
                            type="product" 
                            currentImage={imageVal} 
                            onUpload={(file, url) => {
                              if (onImageUpload) onImageUpload(imageKey, file, url);
                              else onEditValueChange(imageKey, url, 20000000);
                            }} 
                          />
                        </div>
                      </div>
                    ) : (
                      <div className="w-full h-32 mb-3 bg-gray-50 rounded-lg overflow-hidden flex items-center justify-center border border-gray-100">
                        {parsed.image_status === 'pending' ? (
                          <div className="w-full h-full flex flex-col items-center justify-center text-[#0b7f8f] gap-2 bg-gray-50 animate-pulse">
                            <i className="ri-loader-4-line animate-spin text-2xl"></i>
                            <span className="text-[10px] font-medium text-gray-400">Generating...</span>
                          </div>
                        ) : parsed.image ? (
                          <img src={parsed.image} alt={parsed.name || 'Product'} className="w-full h-full object-cover" />
                        ) : (
                          <i className="ri-image-line text-gray-300 text-2xl"></i>
                        )}
                      </div>
                    )
                  ) : (
                    <div className="w-8 h-8 mb-2 bg-[#0b7f8f]/5 rounded-lg flex items-center justify-center">
                      <i className={`${iconVal || 'ri-briefcase-line'} text-[#0b7f8f] text-sm`}></i>
                    </div>
                  )}

                  {editing ? (
                    <div className="space-y-2">
                      <input
                        className={`w-full text-sm font-semibold text-gray-900 rounded px-2 py-1 focus:outline-none border ${getInputClass(nameKey, '')}`}
                        value={nameVal}
                        onChange={(e) => onEditValueChange(nameKey, e.target.value, LIMITS.cardName.max)}
                        maxLength={LIMITS.cardName.max}
                        placeholder={`${label} name`}
                      />
                      <CharLimitLabel current={nameVal.length} min={LIMITS.cardName.min} max={LIMITS.cardName.max} />
                      {errors[nameKey] && (
                        <p className="text-[11px] text-red-500 flex items-center gap-1">
                          <i className="ri-error-warning-line"></i>{errors[nameKey].message}
                        </p>
                      )}
                      <textarea
                        className={`w-full text-xs text-gray-600 rounded px-2 py-1 focus:outline-none resize-none border ${getInputClass(descKey, '')}`}
                        rows={2}
                        value={descVal}
                        onChange={(e) => onEditValueChange(descKey, e.target.value, LIMITS.cardDesc.max)}
                        maxLength={LIMITS.cardDesc.max}
                        placeholder="Short description"
                      />
                      <CharLimitLabel current={descVal.length} min={LIMITS.cardDesc.min} max={LIMITS.cardDesc.max} />
                      {errors[descKey] && (
                        <p className="text-[11px] text-red-500 flex items-center gap-1">
                          <i className="ri-error-warning-line"></i>{errors[descKey].message}
                        </p>
                      )}
                      <input
                        className={`w-full text-xs text-gray-500 rounded px-2 py-1 focus:outline-none border ${getInputClass(urlKey, '')}`}
                        value={urlVal}
                        onChange={(e) => onEditValueChange(urlKey, e.target.value, LIMITS.cardUrl.max)}
                        maxLength={LIMITS.cardUrl.max}
                        placeholder="URL slug or link"
                      />
                      {!isProduct && (
                        <div className="mt-1">
                          <label className="block text-xs text-gray-500 mb-1">Icon</label>
                          <IconPicker
                            value={iconVal}
                            onChange={(val) => onEditValueChange(iconKey, val, 50)}
                            className={getInputClass(iconKey, '')}
                          />
                        </div>
                      )}
                    </div>
                  ) : (
                    <>
                      <h4 className="text-sm font-semibold text-gray-900 mb-1">{String(parsed.name || '')}</h4>
                      <p className="text-xs text-gray-600 leading-relaxed mb-2">{String(parsed.description || '')}</p>
                      <span className="text-[11px] text-gold font-medium">{String(parsed.url || '')}</span>
                    </>
                  )}
                </div>
              );
            })}
          </div>
          {editing && onAddItem && (
            <button
              onClick={onAddItem}
              className="mt-3 w-full py-2.5 border border-dashed border-gray-300 rounded-lg text-sm text-gray-500 hover:text-gold hover:border-gold transition-colors flex items-center justify-center gap-2 cursor-pointer"
            >
              <i className="ri-add-line"></i>
              Add {label}
            </button>
          )}
          {editing && (
            <div className="text-xs text-gray-500 mt-1">
              {cards.length} {label.toLowerCase()}s
            </div>
          )}
        </div>
      );
    }

    case 'testimonial': {
      const quotes = Array.isArray(section.content) ? section.content : [];
      if (quotes.length === 0 && !editing) {
        return (
          <div className="bg-gray-50/70 border border-dashed border-gray-200 rounded-xl p-6 flex flex-col items-center justify-center text-center">
            <p className="text-xs text-gray-500">No testimonials configured yet. Click "Generate Section" or "Edit" to add testimonials.</p>
          </div>
        );
      }
      return (
        <div>
          {quotes.length === 0 && editing && (
            <div className="bg-gray-50/70 border border-dashed border-gray-200 rounded-xl p-6 flex flex-col items-center justify-center text-center mb-3">
              <p className="text-xs text-gray-500">No testimonials in this section yet.</p>
            </div>
          )}
          <div className="grid grid-cols-3 gap-3">
            {quotes.map((q, idx) => {
              const parsed = parseCardSafely(q, { quote: typeof q === 'string' ? q : '', client: '', role: '' });
              const quoteKey = `test_${idx}_quote`;
              const clientKey = `test_${idx}_client`;
              const roleKey = `test_${idx}_role`;
              const quoteVal = String(editing ? (editValues[quoteKey] ?? parsed.quote) : parsed.quote || '');
              const clientVal = String(editing ? (editValues[clientKey] ?? parsed.client) : parsed.client || '');
              const roleVal = String(editing ? (editValues[roleKey] ?? parsed.role) : parsed.role || '');

              return (
                <div key={idx} className="bg-white border border-gray-200 rounded-lg p-4 relative group">
                  {editing && onDeleteItem && (
                    <button
                      onClick={() => onDeleteItem(idx)}
                      className="absolute top-2 right-2 w-6 h-6 flex items-center justify-center rounded-full bg-red-50 text-red-500 hover:bg-red-100 transition-colors cursor-pointer opacity-0 group-hover:opacity-100 z-10"
                      title="Delete testimonial"
                    >
                      <i className="ri-delete-bin-line text-xs"></i>
                    </button>
                  )}
                  <div className="w-8 h-8 mb-3 bg-navy/5 rounded-lg flex items-center justify-center">
                    <i className="ri-double-quotes-l text-navy text-sm"></i>
                  </div>
                  {editing ? (
                    <div className="space-y-2">
                      <textarea
                        className={`w-full text-sm text-gray-700 rounded px-2 py-1 focus:outline-none resize-none leading-relaxed border ${getInputClass(quoteKey, '')}`}
                        rows={3}
                        value={quoteVal}
                        onChange={(e) => onEditValueChange(quoteKey, e.target.value, LIMITS.testimonialQuote.max)}
                        maxLength={LIMITS.testimonialQuote.max}
                        placeholder="Client quote"
                      />
                      <CharLimitLabel current={quoteVal.length} min={LIMITS.testimonialQuote.min} max={LIMITS.testimonialQuote.max} />
                      {errors[quoteKey] && (
                        <p className="text-[11px] text-red-500 flex items-center gap-1">
                          <i className="ri-error-warning-line"></i>{errors[quoteKey].message}
                        </p>
                      )}
                      <input
                        className={`w-full text-xs font-medium text-gray-900 rounded px-2 py-1 focus:outline-none border ${getInputClass(clientKey, '')}`}
                        value={clientVal}
                        onChange={(e) => onEditValueChange(clientKey, e.target.value, LIMITS.testimonialClient.max)}
                        maxLength={LIMITS.testimonialClient.max}
                        placeholder="Client name"
                      />
                      <input
                        className={`w-full text-xs text-gray-500 rounded px-2 py-1 focus:outline-none border ${getInputClass(roleKey, '')}`}
                        value={roleVal}
                        onChange={(e) => onEditValueChange(roleKey, e.target.value, LIMITS.testimonialRole.max)}
                        maxLength={LIMITS.testimonialRole.max}
                        placeholder="Role / Company"
                      />
                    </div>
                  ) : (
                    <>
                      <p className="text-sm text-gray-700 leading-relaxed mb-3 italic">"{String(parsed.quote || '')}"</p>
                      <div className="pt-3 border-t border-gray-100">
                        <p className="text-xs font-medium text-gray-900">{String(parsed.client || '')}</p>
                        <p className="text-[11px] text-gray-500">{String(parsed.role || '')}</p>
                      </div>
                    </>
                  )}
                </div>
              );
            })}
          </div>
          {editing && onAddItem && (
            <button
              onClick={onAddItem}
              className="mt-3 w-full py-2.5 border border-dashed border-gray-300 rounded-lg text-sm text-gray-500 hover:text-gold hover:border-gold transition-colors flex items-center justify-center gap-2 cursor-pointer"
            >
              <i className="ri-add-line"></i>
              Add Testimonial
            </button>
          )}
          {editing && (
            <div className="text-xs text-gray-500 mt-1">
              {quotes.length} testimonials
            </div>
          )}
        </div>
      );
    }

    case 'clientLogos': {
      const logos = Array.isArray(section.content) ? section.content : [];
      if (logos.length === 0 && !editing) {
        return (
          <div className="bg-gray-50/70 border border-dashed border-gray-200 rounded-xl p-6 flex flex-col items-center justify-center text-center">
            <p className="text-xs text-gray-500">No client logos configured yet. Click "Generate Section" or "Edit" to add logos.</p>
          </div>
        );
      }
      return (
        <div>
          {logos.length === 0 && editing && (
            <div className="bg-gray-50/70 border border-dashed border-gray-200 rounded-xl p-6 flex flex-col items-center justify-center text-center mb-3">
              <p className="text-xs text-gray-500">No client logos in this section yet.</p>
            </div>
          )}
          <div className={editing ? "flex flex-col gap-4" : "flex items-center gap-6 flex-wrap"}>
            {logos.map((logo, idx) => {
              const parsed = parseCardSafely(logo, { name: typeof logo === 'string' ? logo : '', logo: '' });
              const nameKey = `client_${idx}_name`;
              const logoKey = `client_${idx}_logo`;
              const nameVal = String(editing ? (editValues[nameKey] ?? parsed.name) : parsed.name || '');
              const logoVal = String(editing ? (editValues[logoKey] ?? parsed.logo) : parsed.logo || '');

              return (
                <div key={idx} className={`flex ${editing ? 'flex-col gap-3 w-full' : 'flex-col items-center gap-2'} bg-white border border-gray-100 rounded-lg p-3 relative group`}>
                  {editing && onDeleteItem && (
                    <button
                      onClick={() => onDeleteItem(idx)}
                      className="absolute -top-2 -right-2 w-6 h-6 flex items-center justify-center rounded-full bg-red-50 text-red-500 hover:bg-red-100 transition-colors cursor-pointer opacity-0 group-hover:opacity-100 z-10"
                      title="Delete logo"
                    >
                      <i className="ri-delete-bin-line text-xs"></i>
                    </button>
                  )}
                  
                  {editing ? (
                    <div className="space-y-3 w-full">
                      <div className="flex flex-col gap-2">
                        {logoVal && (
                          <div className="w-[100px] h-[50px] bg-white rounded border border-gray-100 flex items-center justify-center overflow-hidden">
                            <img src={logoVal} alt={nameVal} className="max-w-full max-h-full object-contain" />
                          </div>
                        )}
                        <ImageUploader 
                          type="clientLogo" 
                          currentImage={logoVal} 
                          onUpload={(file, url) => {
                            if (onImageUpload) onImageUpload(logoKey, file, url);
                            else onEditValueChange(logoKey, url, 20000000);
                          }} 
                        />
                      </div>
                      <input
                        className={`w-full text-sm text-gray-900 rounded px-2 py-1 focus:outline-none border ${getInputClass(nameKey, '')}`}
                        value={nameVal}
                        onChange={(e) => onEditValueChange(nameKey, e.target.value, LIMITS.clientName.max)}
                        maxLength={LIMITS.clientName.max}
                        placeholder="Client Name"
                      />
                      <CharLimitLabel current={nameVal.length} min={LIMITS.clientName.min} max={LIMITS.clientName.max} />
                    </div>
                  ) : (
                    <>
                      {parsed.logo_status === 'pending' ? (
                        <div className="w-[100px] h-[50px] bg-gray-50 rounded border border-gray-100 flex items-center justify-center text-[#0b7f8f] animate-pulse">
                          <i className="ri-loader-4-line animate-spin text-lg"></i>
                        </div>
                      ) : parsed.logo ? (
                        <div className="w-[100px] h-[50px] bg-white rounded border border-gray-100 flex items-center justify-center overflow-hidden">
                          <img src={parsed.logo} alt={String(parsed.name || '')} className="max-w-full max-h-full object-contain" />
                        </div>
                      ) : (
                        <div className="w-[100px] h-[50px] bg-gray-50 rounded border border-gray-100 flex items-center justify-center text-gray-300">
                          <i className="ri-image-line text-lg"></i>
                        </div>
                      )}
                      <span className="text-[10px] text-gray-400 truncate w-full text-center">{String(parsed.name || '')}</span>
                    </>
                  )}
                </div>
              );
            })}
          </div>
          {editing && onAddItem && (
            <button
              onClick={onAddItem}
              className="mt-4 py-2 px-4 border border-dashed border-gray-300 rounded-lg text-xs text-gray-500 hover:text-gold hover:border-gold transition-colors flex items-center gap-1.5 cursor-pointer"
            >
              <i className="ri-add-line"></i>
              Add Logo
            </button>
          )}
        </div>
      );
    }

    case 'faq': {
      const faqs = Array.isArray(section.content) ? section.content : [];
      if (faqs.length === 0 && !editing) {
        return (
          <div className="bg-gray-50/70 border border-dashed border-gray-200 rounded-xl p-6 flex flex-col items-center justify-center text-center">
            <p className="text-xs text-gray-500">No FAQs configured yet. Click "Generate Section" or "Edit" to add questions and answers.</p>
          </div>
        );
      }
      return (
        <div className="space-y-2">
          {faqs.length === 0 && editing && (
            <div className="bg-gray-50/70 border border-dashed border-gray-200 rounded-xl p-6 flex flex-col items-center justify-center text-center mb-3">
              <p className="text-xs text-gray-500">No FAQs in this section yet.</p>
            </div>
          )}
          {faqs.map((f, idx) => {
            const parsed = parseCardSafely(f, { question: typeof f === 'string' ? f : '', answer: '' });
            const qKey = `faq_${idx}_q`;
            const aKey = `faq_${idx}_a`;
            const qVal = String(editing ? (editValues[qKey] ?? parsed.question) : parsed.question || '');
            const aVal = String(editing ? (editValues[aKey] ?? parsed.answer) : parsed.answer || '');
            const isOpen = openFaq === idx;

            if (editing) {
              return (
                <div key={idx} className="bg-white border border-gray-200 rounded-lg p-3 space-y-2 relative group">
                  {onDeleteItem && (
                    <button
                      onClick={() => onDeleteItem(idx)}
                      className="absolute top-2 right-2 w-6 h-6 flex items-center justify-center rounded-full bg-red-50 text-red-500 hover:bg-red-100 transition-colors cursor-pointer opacity-0 group-hover:opacity-100 z-10"
                      title="Delete FAQ"
                    >
                      <i className="ri-delete-bin-line text-xs"></i>
                    </button>
                  )}
                  <input
                    className={`w-full text-sm font-medium text-gray-900 rounded px-2 py-1 focus:outline-none border ${getInputClass(qKey, '')}`}
                    value={qVal}
                    onChange={(e) => onEditValueChange(qKey, e.target.value, LIMITS.faqQuestion.max)}
                    maxLength={LIMITS.faqQuestion.max}
                    placeholder="Question"
                  />
                  <CharLimitLabel current={qVal.length} min={LIMITS.faqQuestion.min} max={LIMITS.faqQuestion.max} />
                  {errors[qKey] && (
                    <p className="text-[11px] text-red-500 flex items-center gap-1">
                      <i className="ri-error-warning-line"></i>{errors[qKey].message}
                    </p>
                  )}
                  <textarea
                    className={`w-full text-xs text-gray-600 rounded px-2 py-1 focus:outline-none resize-none leading-relaxed border ${getInputClass(aKey, '')}`}
                    rows={3}
                    value={aVal}
                    onChange={(e) => onEditValueChange(aKey, e.target.value, LIMITS.faqAnswer.max)}
                    maxLength={LIMITS.faqAnswer.max}
                    placeholder="Answer"
                  />
                  <CharLimitLabel current={aVal.length} min={LIMITS.faqAnswer.min} max={LIMITS.faqAnswer.max} />
                  {errors[aKey] && (
                    <p className="text-[11px] text-red-500 flex items-center gap-1">
                      <i className="ri-error-warning-line"></i>{errors[aKey].message}
                    </p>
                  )}
                </div>
              );
            }

            return (
              <div key={idx} className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                <button
                  onClick={() => setOpenFaq(isOpen ? null : idx)}
                  className="w-full flex items-center justify-between px-4 py-3 text-left cursor-pointer hover:bg-gray-50/50 transition-colors"
                >
                  <span className="text-sm font-medium text-gray-900">{String(parsed.question || '')}</span>
                  <i className={`ri-arrow-down-s-line text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`}></i>
                </button>
                {isOpen && (
                  <div className="px-4 pb-3 text-sm text-gray-600 leading-relaxed">
                    {String(parsed.answer || '')}
                  </div>
                )}
              </div>
            );
          })}
          {editing && onAddItem && (
            <button
              onClick={onAddItem}
              className="mt-3 w-full py-2.5 border border-dashed border-gray-300 rounded-lg text-sm text-gray-500 hover:text-gold hover:border-gold transition-colors flex items-center justify-center gap-2 cursor-pointer"
            >
              <i className="ri-add-line"></i>
              Add FAQ
            </button>
          )}
          {editing && (
            <div className="text-xs text-gray-500 mt-1">
              {faqs.length} FAQs
            </div>
          )}
        </div>
      );
    }

    default:
      return <p className="text-sm text-gray-600">Content preview</p>;
  }
}